import React, { useMemo, useState } from 'react';
import { candidatasParaInclusao, diferencaComposicao } from './composicaoGrupo';
import type { MatrizContaContabil } from './models';
import CampoJustificativaSox from './CampoJustificativaSox';
import { useJustificativaSugerida } from './useJustificativaSugerida';
import { justificativaComposicaoGrupo } from './justificativaSox';

export interface ContaDoGrupo {
  contaId: number;
  contaCodigo: string;
  contaDescricao: string;
}

interface Props {
  grupoNome: string;
  contasDoGrupo: ContaDoGrupo[];
  /** Contas elegíveis ao mapeamento (a listagem já vem sem contas de resultado). */
  contasElegiveis: MatrizContaContabil[];
  /** Grupo atual de cada conta já mapeada — avisa quando a inclusão move a conta de outro grupo. */
  grupoPorConta: Map<number, string>;
  apenasClientes: boolean;
  podeSerCliente: (conta: MatrizContaContabil) => boolean;
  salvando: boolean;
  onSalvar: (contaIds: number[], justificativaSox: string) => void;
  onFechar: () => void;
}

const EditarComposicaoGrupoModal: React.FC<Props> = ({
  grupoNome, contasDoGrupo, contasElegiveis, grupoPorConta, apenasClientes, podeSerCliente,
  salvando, onSalvar, onFechar,
}) => {
  const original = useMemo(() => contasDoGrupo.map(c => c.contaId), [contasDoGrupo]);
  const [selecionadas, setSelecionadas] = useState<Set<number>>(() => new Set(original));
  const [busca, setBusca] = useState('');

  const dadosDaConta = useMemo(() => {
    const m = new Map<number, { codigo: string; descricao: string }>();
    contasElegiveis.forEach(c => m.set(c.contaId, { codigo: c.codigo, descricao: c.descricao }));
    contasDoGrupo.forEach(c => m.set(c.contaId, { codigo: c.contaCodigo, descricao: c.contaDescricao }));
    return m;
  }, [contasElegiveis, contasDoGrupo]);

  const elegiveisDoGrupo = useMemo(
    () => (apenasClientes ? contasElegiveis.filter(podeSerCliente) : contasElegiveis),
    [apenasClientes, contasElegiveis, podeSerCliente]);

  const candidatas = candidatasParaInclusao(elegiveisDoGrupo, selecionadas, busca);
  const diff = diferencaComposicao(original, selecionadas);
  // Justificativa sugerida: cita as contas incluídas e removidas e acompanha as escolhas até a pessoa editar.
  const codigoDe = (id: number) => dadosDaConta.get(id)?.codigo ?? String(id);
  const justificativa = useJustificativaSugerida(
    justificativaComposicaoGrupo({ grupo: grupoNome, incluidas: diff.adicionadas.map(codigoDe), removidas: diff.removidas.map(codigoDe) }));
  const podeSalvar = diff.houveMudanca && selecionadas.size > 0 && justificativa.texto.trim().length > 0 && !salvando;

  const alternar = (id: number) =>
    setSelecionadas(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <div className='wf-modal-bg'>
      <div className='wf-modal wf-modal--wide'>
        <div className='wf-modal__header'>
          <div>
            <h2>Editar contas do grupo</h2>
            <p>{grupoNome}{apenasClientes ? ' · somente contas de Clientes' : ''}</p>
          </div>
          <button onClick={onFechar} aria-label='Fechar'>×</button>
        </div>

        <div className='wf-modal__body'>
          <div>
            <strong style={{ fontSize: 13 }}>Contas vinculadas ({selecionadas.size})</strong>
            <div style={{ maxHeight: 180, overflowY: 'auto', marginTop: 6 }}>
              {Array.from(selecionadas).map(id => {
                const d = dadosDaConta.get(id);
                return (
                  <div key={id} style={{ display: 'flex', gap: 8, padding: '3px 0', fontSize: 13 }}>
                    <code>{d?.codigo ?? id}</code>
                    <span style={{ flex: 1 }}>{d?.descricao}</span>
                    <button className='wf-link' onClick={() => alternar(id)}>Remover do grupo</button>
                  </div>
                );
              })}
              {selecionadas.size === 0 && (
                <p style={{ color: '#f1416c', fontSize: 12 }}>
                  O grupo precisa manter ao menos uma conta. Para removê-lo por completo, use "Excluir".
                </p>
              )}
            </div>
          </div>

          <div>
            <strong style={{ fontSize: 13 }}>Incluir contas</strong>
            <input
              style={{ marginTop: 6 }}
              placeholder='Buscar por código ou descrição...'
              value={busca}
              onChange={e => { const v = e.target.value; setBusca(v); }}
            />
            <div style={{ maxHeight: 180, overflowY: 'auto', marginTop: 6 }}>
              {candidatas.itens.map(c => {
                const outroGrupo = grupoPorConta.get(c.contaId);
                return (
                  <div key={c.contaId} style={{ display: 'flex', gap: 8, padding: '3px 0', fontSize: 13 }}>
                    <code>{c.codigo}</code>
                    <span style={{ flex: 1 }}>
                      {c.descricao}
                      {outroGrupo && <em style={{ color: '#a1800c' }}> (hoje em "{outroGrupo}" — será movida)</em>}
                    </span>
                    <button className='wf-link' onClick={() => alternar(c.contaId)}>Incluir no grupo</button>
                  </div>
                );
              })}
              {candidatas.itens.length === 0 && (
                <p style={{ color: '#a1a5b7', fontSize: 12 }}>Nenhuma conta elegível encontrada.</p>
              )}
              {candidatas.total > candidatas.itens.length && (
                <p style={{ color: '#a1a5b7', fontSize: 12 }}>
                  Mostrando {candidatas.itens.length} de {candidatas.total}. Refine a busca para ver as demais.
                </p>
              )}
            </div>
          </div>

          <label>
            Justificativa SOX (obrigatória)
            <CampoJustificativaSox justificativa={justificativa} rows={3} placeholder='Motivo da alteração na composição do grupo...' />
          </label>
          <p style={{ margin: 0, fontSize: 12, color: '#7e8299' }}>
            {diff.adicionadas.length} conta(s) a incluir · {diff.removidas.length} a remover.
            Remover desfaz apenas o vínculo com o grupo; a conta contábil e seus lançamentos não são afetados.
          </p>
        </div>

        <div className='wf-modal__footer'>
          <button className='wf-btn wf-btn--secondary' onClick={onFechar} disabled={salvando}>Cancelar</button>
          <button className='wf-btn wf-btn--primary' onClick={() => onSalvar(Array.from(selecionadas), justificativa.texto.trim())} disabled={!podeSalvar}>
            {salvando ? 'Salvando...' : 'Salvar composição'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditarComposicaoGrupoModal;
